import type { Moment } from "moment";
import type { MarketingAction } from "src/types/action/MarketingAction";
import type { ChannelCategory, ChannelSourceEnum } from "src/types/channel";
import type { AutoRulesComparePeriodEnum } from "src/types/enum/AutoRulesComparePeriodEnum";
import type { DayOfTheWeek } from "src/types/enum/DayOfTheWeek";
import type { MarketingActionType } from "src/types/enum/MarketingActionType";
import type { PushTriggerModeEnum } from "src/types/enum/PushTriggerModeEnum";
import type { OfferAudience, OfferDiscount, OfferTypeEnum } from "src/types/offer";
import type { CategoryItemList } from "src/types/offer/CategoryItemList";
import type { ConcurrentAction, OverrideStrategyEnum } from "src/types/offer/ConcurrentAction";
import type { ItemNameCategoryDiscount } from "src/types/offer/ItemNameCategoryDiscount";
import type { Pos } from "src/types/pos";
import type { Trigger, TriggerMetricEnum } from "src/types/trigger";
import type { RecurrenceFrequence } from "src/views/marketing-actions/components/form";
import type { CustomScenario } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard";
import type { OfferDiscountType } from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.formator";
import type { TriggerStatus } from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormOutput";

export interface ChannelSource {
    source: ChannelSourceEnum;
    offerAudiences: OfferAudience[];
    sponsoredAdAudiences: OfferAudience[];
    authorizedMarketingAction: MarketingActionType[];
    sponsoredAdAutoBiddingPosIds: number[];
}

export interface MarketingActionConditionInput {
    triggerStart: Partial<Trigger> | null;
    triggerStopStatus?: TriggerStatus | null;
    triggerStartStatus?: TriggerStatus | null;
    triggerStop: Partial<Trigger> | null;
}

export interface MarketingActionInputValues extends MarketingAction {
    originalOfferId?: number;
    originalSponsoredAdId?: number;
    duo?: MarketingActionInputValues;
    pushTriggerMode?: PushTriggerModeEnum;
}

export interface MarketingActionRecurrenceInput {
    defaultDisabledField: string[];
    ending: "forever" | "date";
    startTime: number | null;
    endTime: number | null;
    startDate: Moment;
    endDate: Moment;
    days: DayOfTheWeek[];
    frequence: RecurrenceFrequence;
}

export enum BudgetComputeMethodEnum {
    Slot = "slot",
    Week = "week",
    Total = "total",
}

export interface MarketingActionAudienceInput {
    value: string;
}

export interface MarketingActionScenarioCharacteristicInput
    extends Partial<MarketingActionConditionInput> {
    audience: MarketingActionAudienceInput;
}

export interface SponsoredAdScenarioCharacteristicInput
    extends MarketingActionScenarioCharacteristicInput {
    isBidAutoEnabled: { [key: string]: boolean; default: boolean };
    bid: {
        value: number;
        min: number;
        max: number;
    };
    slotBudget: {
        value: number;
        min: number;
        max: number;
    };
    weeklyBudget: {
        value: number;
        min: number;
        max: number;
    };
    totalBudget: {
        value: number;
        min: number;
        max: number;
    };
    budgetComputeMethod: BudgetComputeMethodEnum;
}

export interface SponsoredAdCharacteristicInput {
    scenarios: SponsoredAdScenarioCharacteristicInput[];
}

export interface OfferScenarioCharacteristicInput
    extends MarketingActionScenarioCharacteristicInput {
    value: OfferTypeEnum;
    typeDiscount?: OfferDiscountType;
    products: ItemNameCategoryDiscount[];
    categoryItemList: Partial<CategoryItemList>[];
    cart: number | null;
    discount?: string | null;
    specialOperation: OfferDiscount | null;
    specialExistOnFlynt?: boolean | null;
    isSpecialOperation?: boolean;
    isSpecialString?: string | "false";
    isCoFunded: boolean;
    isCreationSpecialOperation?: boolean;
}

export interface OfferCharacteristicInput {
    specialOperation?: OfferDiscount | null;
    specialExistOnFlynt?: boolean | null;
    isSpecialOperation?: boolean;
    isSpecialString?: string | "false";
    isCoFunded: boolean;
    scenarios: OfferScenarioCharacteristicInput[];
}

export interface MarketingActionServiceChannelInput {
    service: {
        value: ChannelSourceEnum;
    };
    channel: {
        value: ChannelCategory | null;
    };
}

export interface MarketingActionModalInput {
    overrideStrategy: OverrideStrategyEnum;
    overrides: ConcurrentAction[];
    unavailablePosIds: number[];
    invalidPosIds: number[];
    confirmMissingProducts: boolean;
    confirmOpeningHours: boolean;
    confirmCashierUnsync: boolean;
    preorder: boolean;
}

export interface MarketingActionOtherInput {
    confirmOpeningHours: boolean;
    confirmCashierUnsync: boolean;
    confirm: boolean;
}

export interface MarketingActionFormInput {
    originalSponsoredAdId?: number | null;
    originalOfferId?: number | null;
    posList: Pos[];
    originalPoss?: number[] | null;
    recurrence: MarketingActionRecurrenceInput;
    sponsoredAdCharacteristic: SponsoredAdScenarioCharacteristicInput;
    offerCharacteristic: OfferScenarioCharacteristicInput;
    serviceAndChannel: MarketingActionServiceChannelInput;
    modal: MarketingActionModalInput;
    condition: MarketingActionConditionInput;
    confirmOpeningHours: boolean;
    confirmCashierUnsync: boolean;
    confirm: boolean;
    audience: MarketingActionAudienceInput;
    variants?: string[];
    scenarios?: CustomScenario[];
    scenarioBasicConfig: {
        metric: TriggerMetricEnum;
        channelIds: number[];
        comparePeriod: AutoRulesComparePeriodEnum;
        scenarioSpecificActivation?: {
            peakScenarioEnabled: boolean;
            firstSlotScenarioEnabled: boolean;
            peakScenarioUuid?: string;
            firstSlotScenarioUuid?: string;
        };
        startDateComparePeriod?: string;
        endDateComparePeriod?: string;
    };
    pushTriggerMode?: PushTriggerModeEnum;
}
